import React, { useCallback, useEffect, useState } from 'react';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { Link } from 'react-router-dom';
import OrgSubscriptionPanel from '@/components/billing/OrgSubscriptionPanel';
import { usageService, type OrgUsageSummary } from '@/api/services/usage';
import { formatApiError } from '@/utils/formatApiError';
import { entitlementStatusMessage } from '@/utils/planWarning';
import { LIFECYCLE_STATE_LABELS } from '@/utils/lifecycleReasons';
import { subscriptionService, type OrganizationHealth, type SubscriptionStatus } from '@/api/services/notifications';
import { useMe } from '@/context/MeContext';

function UsageMeter({
  label,
  used,
  limit,
}: {
  label: string;
  used: number;
  limit?: number | null;
}) {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : null;
  const over = limit != null && limit > 0 && used >= limit;
  const limitLabel = limit != null ? ` / ${limit}` : ' / Unlimited';
  return (
    <div className={`usage-meter${over ? ' usage-meter--over' : ''}`}>
      <div className="usage-meter__label">
        <span>{label}</span>
        <span>
          {used}
          {limitLabel}
        </span>
      </div>
      {pct != null ? (
        <div className="usage-meter__bar">
          <div className="usage-meter__fill" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export default function UsagePage() {
  const { me } = useMe();
  const [usage, setUsage] = useState<OrgUsageSummary | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [health, setHealth] = useState<OrganizationHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [u, sub, h] = await Promise.all([
        usageService.summary('current_month'),
        subscriptionService.status().catch(() => null),
        subscriptionService.health().catch(() => null),
      ]);
      setUsage(u);
      setSubscription(sub);
      setHealth(h);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <div className="panel">Loading usage…</div>;
  if (!usage) {
    const message =
      error?.includes('No active workspace') || error?.includes('workspace')
        ? 'No active workspace found for this account.'
        : error?.includes('403') || error?.toLowerCase().includes('permission')
          ? 'You do not have permission to view usage.'
          : (error ?? 'Usage is not available yet.');
    return <div className="panel">{message}</div>;
  }

  const statusMsg = entitlementStatusMessage(usage, {
    canActOnSearchQuota: me?.permissions.canCreateSearch ?? false,
  });
  const canManageSubscription =
    (me?.permissions.canManageOrgSubscription ?? false) ||
    (me?.permissions.canUseBillingCheckout ?? false);
  const planLabel = usage.unlimited
    ? 'Internal (Unlimited)'
    : formatPlanDisplayName(usage.planSlug);
  const notificationsOver =
    usage.limits.maxNotificationsPerDay != null &&
    usage.limits.maxNotificationsPerDay > 0 &&
    usage.usage.notificationsSentToday >= usage.limits.maxNotificationsPerDay;

  return (
    <div>
      <div className="panel">
        <h1>Usage &amp; plan</h1>
        {statusMsg ? (
          <div className="alert alert-warning usage-quota-alert">
            <span>{statusMsg}</span>
            <div className="table-action-bar">
              {canManageSubscription ? (
                <Link className="btn btn-sm btn-primary" to="/usage#manage-subscription">
                  Manage subscription
                </Link>
              ) : null}
              {me?.permissions.canViewCustomerBilling ? (
                <Link className="btn btn-sm" to="/billing">
                  View billing
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
        {notificationsOver ? (
          <div className="alert alert-warning usage-quota-alert">
            <span>
              Notifications today {usage.usage.notificationsSentToday} / {usage.limits.maxNotificationsPerDay}
              — daily limit reached.
            </span>
            <div className="table-action-bar">
              {canManageSubscription ? (
                <Link className="btn btn-sm btn-primary" to="/usage#manage-subscription">
                  Manage subscription
                </Link>
              ) : null}
              {me?.permissions.canViewCustomerBilling ? (
                <Link className="btn btn-sm" to="/billing">
                  View billing
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
        {error ? <div className="alert alert-error">{error}</div> : null}

        <div className="usage-summary-grid">
          <p>
            Plan: <strong>{planLabel}</strong>
          </p>
          {subscription ? (
            <>
              <p>
                Status:{' '}
                <strong>
                  {LIFECYCLE_STATE_LABELS[subscription.lifecycleState] ?? subscription.lifecycleLabel}
                </strong>
                {subscription.paymentStatus === 'failed' ? ' · payment failed' : ''}
              </p>
              {subscription.expiresAt ? (
                <p className="muted">
                  Expiry: {new Date(subscription.expiresAt).toLocaleDateString()}
                  {subscription.graceUntil
                    ? ` · grace until ${new Date(subscription.graceUntil).toLocaleDateString()}`
                    : ''}
                </p>
              ) : null}
              <p className="muted">
                Auto-renew: {subscription.autoRenew ? 'Yes' : 'No'} · Payment:{' '}
                {subscription.paymentStatus === 'failed' ? 'Failed' : 'Healthy'}
              </p>
            </>
          ) : (
            <p className="muted">
              Status:{' '}
              {usage.unlimited
                ? 'Internal · unlimited'
                : (usage.entitlementStatus ?? (usage.subscriptionActive ? 'active' : 'inactive'))}
            </p>
          )}
          {health ? (
            <p className="muted">
              Account health: <strong>{health.health}</strong>
            </p>
          ) : null}
          {usage.expiresAt && !subscription?.expiresAt ? (
            <p className="muted">Expires {new Date(usage.expiresAt).toLocaleDateString()}</p>
          ) : null}

          <UsageMeter label="Active searches" used={usage.usage.activeSearches} limit={usage.limits.maxSearches} />
          <UsageMeter
            label="Recipients"
            used={usage.usage.recipients}
            limit={usage.limits.maxNotificationRecipients}
          />
          <UsageMeter
            label="Notifications today"
            used={usage.usage.notificationsSentToday}
            limit={usage.limits.maxNotificationsPerDay}
          />
          {usage.limits.minScrapeIntervalMinutes ? (
            <p className="muted">Minimum scrape interval: {usage.limits.minScrapeIntervalMinutes} minutes</p>
          ) : null}
          <p className="muted">Alert subscriptions: {usage.subscriptions}</p>
        </div>
      </div>

      <OrgSubscriptionPanel usage={usage} subscription={subscription} onChanged={() => void load()} />
    </div>
  );
}
